import type { CSSProperties } from 'react'
import React from 'react'
import type {
  ClearIndicatorProps,
  ControlProps,
  GroupBase,
  StylesConfig
} from 'react-select'
import { components as SelectComponents } from 'react-select'
import type { AsyncProps as SelectProps } from 'react-select/async'
import AsyncReactSelect from 'react-select/async'
import type { USSchool } from '@mote/api'
import { CloseIcon, SearchIcon } from '@mote/assets/icons'
import { Block } from '@mote/ui'
import { useTheme } from '@xstyled/styled-components'

const StyledSelectControl = ({
  children,
  ...props
}: ControlProps<USSchool, false>) => {
  return (
    <SelectComponents.Control {...props}>
      <Block layout='horizontal' align='center' gap={4} maxHeight={26} spreadX>
        <SearchIcon />
        {children}
      </Block>
    </SelectComponents.Control>
  )
}

const StyledSelectClearIndicator = (
  props: ClearIndicatorProps<USSchool, false>
) => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps }
  } = props
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props) as CSSProperties}
    >
      <CloseIcon />
    </div>
  )
}

export const AsyncSelect = (
  props: SelectProps<USSchool, false, GroupBase<USSchool>>
) => {
  const theme = useTheme()
  const styles: StylesConfig<USSchool, false> = {
    option: (styles, { isFocused, isSelected }) => {
      return {
        ...styles,
        fontSize: theme.fontSizes.md,
        backgroundColor: isSelected
          ? theme.colors.primary.base
          : isFocused
          ? theme.colors.grey.l4
          : undefined,
        color: isSelected ? theme.colors.white : theme.colors.black,
        cursor: 'pointer',
        ':active': {
          ...styles[':active'],
          color: isSelected ? theme.colors.white : theme.colors.primary.d1,
          backgroundColor: isSelected
            ? theme.colors.primary.base
            : theme.colors.grey.l4
        }
      }
    },
    control: (styles, { isFocused, isDisabled }) => ({
      ...styles,
      backgroundColor: isDisabled
        ? theme.colors.disabled.l1
        : theme.colors.white,
      pointerEvents: isDisabled ? 'none' : 'auto',
      color: isDisabled ? theme.colors.disabled.l2 : theme.colors.black,
      cursor: 'pointer',
      borderRadius: theme.radii.md,
      borderColor: isFocused ? theme.colors.primary.base : theme.colors.grey.l2,
      boxShadow: isFocused ? `0 0 1px ${theme.colors.primary.base}` : 'none',
      padding: `${theme.space[2.5]} ${theme.space[4]}`,
      fontSize: theme.fontSizes.md,
      lineHeight: theme.lineHeights.md,
      fontFamily: theme.fonts.sans,
      fontWeight: theme.fontWeights.normal,
      transition: 'all 150ms ease-in',
      ':hover': {
        ...styles[':hover'],
        borderColor: isFocused
          ? theme.colors.primary.base
          : theme.colors.grey.d3,
        boxShadow: isFocused ? `0 0 1px ${theme.colors.primary.base}` : 'none'
      }
    }),
    placeholder: (styles, { isDisabled }) => ({
      ...styles,
      color: isDisabled ? theme.colors.disabled.l2 : theme.colors.grey.d2
    }),
    singleValue: styles => ({
      ...styles,
      color: 'inherit',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 'inherit'
    }),
    menu: styles => ({
      ...styles,
      zIndex: 20,
      border: `1px solid ${theme.colors.grey.l2}`,
      padding: `${theme.space[2]} 0`,
      borderRadius: theme.radii.lg,
      boxShadow: theme.shadows.lg,
      background: theme.colors.white,
      transition: 'all 150ms ease-in'
    }),
    indicatorsContainer: styles => ({
      ...styles,
      color: theme.colors.black
    }),
    container: styles => ({
      ...styles,
      width: '100%'
    }),
    noOptionsMessage: styles => ({
      ...styles,
      textAlign: 'left'
    }),
    loadingMessage: styles => ({
      ...styles,
      textAlign: 'left'
    }),
    valueContainer: styles => ({ ...styles, padding: 0 })
  }

  return (
    <AsyncReactSelect
      styles={styles}
      components={{
        ...SelectComponents,
        Control: StyledSelectControl,
        ClearIndicator: StyledSelectClearIndicator,
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null
      }}
      {...props}
    />
  )
}
